import { ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';

/** Shared class names so every public auth screen uses the same controls. */
export const authInputClass =
  'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-[color:var(--action)] focus:outline-none focus:ring-1 focus:ring-[color:var(--action)]';
export const authPrimaryButtonClass =
  'flex w-full items-center justify-center gap-2 rounded-lg bg-[color:var(--action)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-950';
export const authLinkClass = 'font-medium text-[color:var(--action)] hover:underline';

export function AuthError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-inset ring-red-200">
      {message}
    </div>
  );
}

/** Brand mark + organization name for the public (signed-out) screens. */
export function AuthBrand() {
  const { organization, loading } = useOrganization();
  if (loading) return <div className="h-[116px]" aria-hidden="true" />;
  return (
    <div className="text-center">
      {organization?.image_url ? (
        <img
          src={organization.image_url}
          alt={organization.name}
          className="mx-auto h-16 w-auto max-w-[220px] object-contain"
        />
      ) : (
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[color:var(--action)] text-white dark:text-slate-950">
          {organization ? (
            <span className="text-2xl font-semibold">{organization.name.charAt(0).toUpperCase()}</span>
          ) : (
            <ShieldCheck className="h-8 w-8" aria-hidden="true" />
          )}
        </div>
      )}
      <p className="mt-4 text-lg font-semibold text-slate-900">{organization?.name || 'Portal REPSE'}</p>
      <p className="text-sm text-slate-500">Gestión documental REPSE</p>
    </div>
  );
}

interface AuthLayoutProps {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

export default function AuthLayout({ title, description, children, footer }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col justify-center bg-slate-50 px-4 py-12 text-slate-900 sm:px-6">
      <div className="mx-auto w-full max-w-md">
        <AuthBrand />
        <div className="mt-8 rounded-xl border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-8">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
          {description && <p className="mt-1 text-sm text-slate-600">{description}</p>}
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="mt-6 text-center text-sm text-slate-600">{footer}</div>}
      </div>
    </div>
  );
}
